import React, { useEffect, useState } from 'react';
import {
	CCard, CCardBody, CCardHeader, CFormCheck, CButton, CFormInput, CInputGroup,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody, CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCopy } from '@coreui/icons';
import AxiosInstance from 'src/components/Axios';
import { QRCodeSVG } from 'qrcode.react';

const Settings = () => {
	const [enabled, setEnabled] = useState(null);
	const [checked, setChecked] = useState(null);
	const [qrUri, setQrUri] = useState(null);
	const [secret, setSecret] = useState(null);
	const [confirmCode, setConfirmCode] = useState('');
	const [backupCodes, setBackupCodes] = useState(null);
	const [statusLoading, setStatusLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [confirmError, setConfirmError] = useState(null);
	const [confirming, setConfirming] = useState(false);
	const [secretCopied, setSecretCopied] = useState(false);

	const handleCopySecret = () => {
		navigator.clipboard.writeText(secret);
		setSecretCopied(true);
		setTimeout(() => setSecretCopied(false), 2000);
	};

	useEffect(() => {
		let ignore = false;
		setStatusLoading(true);
		AxiosInstance.get('/2fa/status').then((response) => {
			if (ignore) return;
			setEnabled(response.data.enabled);
			setChecked(response.data.enabled);
		}).finally(() => {
			if (!ignore) setStatusLoading(false);
		});
		return () => {
			ignore = true;
		};
	}, []);

	const handleSave = () => {
		setSaving(true);
		if (checked === false && enabled === true) {
			AxiosInstance.post('/2fa/disable').then(() => {
				setEnabled(false);
				setChecked(false);
				setBackupCodes(null);
			}).finally(() => {
				setSaving(false);
			});
		} else if (checked === true && enabled === false) {
			AxiosInstance.post('/2fa/enable').then((response) => {
				setQrUri(response.data.qrUri);
				setSecret(response.data.secret);
			}).finally(() => {
				setSaving(false);
			});
		}
	};

	const handleConfirm = () => {
		setConfirmError(null);
		setConfirming(true);
		AxiosInstance.post('/2fa/enable/confirm', { code: confirmCode }).then((response) => {
			setEnabled(true);
			setChecked(true);
			setQrUri(null);
			setSecret(null);
			setConfirmCode('');
			setBackupCodes(response.data.backupCodes);
		}).catch((error) => {
			setConfirmError(error.response?.data?.error || 'Не вдалося підтвердити код.');
		}).finally(() => {
			setConfirming(false);
		});
	};

	return (
		<CCard>
			<CCardHeader>Налаштування користувача</CCardHeader>
			<CCardBody>
				<h5>Керування двофакторною авторизацією користувача</h5>
				<p className="text-muted small">Можна увімкнути або вимкнути 2FA лише для поточного авторизованого користувача, який зараз залогінений в адмін-панелі</p>
				<p className="text-danger">
					Поточний стан: {enabled === null ? '...' : enabled ? 'Увімкнено' : 'Вимкнено'}
				</p>
				<CFormCheck
					type="checkbox"
					id="twoFactorCheck"
					checked={!!checked}
					onChange={(e) => setChecked(e.target.checked)}
					label={enabled ? 'Вимкнути двофакторну авторизацію' : 'Увімкнути двофакторну авторизацію'}
				/>
				{statusLoading && (
					<div className="d-flex align-items-center gap-2 mb-3">
						<CSpinner size="sm" color="secondary" />
						<span>Виконується запит за статусом</span>
					</div>
				)}
				<div className="mt-3">
					<CButton color="primary" disabled={statusLoading || saving || !!qrUri || checked === enabled} onClick={handleSave}>
						Зберегти
					</CButton>
					{saving && (
						<span className="d-inline-flex align-items-center gap-2 ms-2">
							<CSpinner size="sm" color="secondary" />
							Виконується запит...
						</span>
					)}
				</div>
				{qrUri && (
					<div className="mt-3">
						<QRCodeSVG value={qrUri} size={200} />
						<p>
							Відскануйте QR-код камерою в застосунку Google Authenticator. Якщо сканування недоступне — відкрийте застосунок Google Authenticator, оберіть «Ввести код вручну» і вставте туди цей ключ (саме в застосунок Google Authenticator, не в поле нижче): <code>{secret}</code>{' '}
							<CButton type="button" color="secondary" variant="outline" size="sm" title="Скопіювати секретний код" onClick={handleCopySecret}>
								<CIcon icon={cilCopy} />
							</CButton>
							{secretCopied && <span className="text-success small ms-2">Скопійовано</span>}
						</p>
						<p className="mt-4 mb-2">Введіть код, який застосунок Google Authenticator згенерував після сканування QR-коду:</p>
						<CInputGroup className="mb-2">
							<CFormInput
								placeholder="Код підтвердження"
								value={confirmCode}
								onChange={(e) => {
									setConfirmCode(e.target.value);
									setConfirmError(null);
								}}
							/>
						</CInputGroup>
						{confirmError && <p className="text-danger small">{confirmError}</p>}
						<CButton color="primary" disabled={confirming || !confirmCode.trim()} onClick={handleConfirm}>Підтвердити</CButton>
						{confirming && (
							<span className="d-inline-flex align-items-center gap-2 ms-2">
								<CSpinner size="sm" color="secondary" />
								Виконується запит...
							</span>
						)}
					</div>
				)}
				{backupCodes && (
					<CAccordion className="mt-3">
						<CAccordionItem>
							<CAccordionHeader>Резервні коди</CAccordionHeader>
							<CAccordionBody>
								<p>
									Використовуйте один із них замість коду з Google Authenticator під час входу (логування в адмін панелі),
									якщо телефон недоступний. Кожен код працює лише один раз. Збережіть їх у надійному
									місці — повторно вони тут не показуються.
								</p>
								<ul>
									{backupCodes.map((code) => (
										<li key={code}><code>{code}</code></li>
									))}
								</ul>
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>
				)}
			</CCardBody>
		</CCard>
	);
};

export default Settings;
